import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";

import { GalleryGrid } from "./gallery-grid/gallery-grid";
import { ImageOnClickHandler } from "./gallery-image/gallery-image";
import { GalleryModal } from "./gallery-modal/gallery-modal";
import { GallerySlider } from "./gallery-slider/gallery-slider";
import * as Styled from "./gallery.styled";
import type { GalleryProps } from "./gallery.types";
import {
  GalleryMediaTypes,
  getFullScreenTrackingLabel,
  getGalleryAssets,
} from "./gallery.utils";
import { comScorePageViewTrack } from "../../utils/tracking/tracking-data-utils";
import { useTracking } from "../../utils/tracking/use-tracking";

export const Gallery = ({
  advertImages,
  spinUrl,
  videoUrl,
  isLeasingPDPPage = false,
}: GalleryProps) => {
  const [showGalleryModal, setShowGalleryModal] = useState<boolean>(false);
  const [isPrintMode, setIsPrintMode] = useState(false);
  const images = getGalleryAssets({ images: advertImages });
  const { trackEvent } = useTracking();
  const [currentSlide, setCurrentSlide] = useState(0);
  const isSlider = useMediaQuery({ query: "(max-width: 768px)" });
  const hasSpin = !!spinUrl;
  const hasVideo = !!videoUrl;
  const showViewMore = !!(images.length > 0 || hasVideo || hasSpin);

  const onImageClick: ImageOnClickHandler = ({ index }) => {
    setShowGalleryModal(true);
    trackEvent({
      trackingLabel: getFullScreenTrackingLabel(GalleryMediaTypes.IMAGE),
    });

    comScorePageViewTrack();
    setCurrentSlide(index);
  };

  const props = {
    images,
    hasSpin,
    hasVideo,
    showViewMore,
    isLeasingPDPPage,
    onImageClick,
  };

  useEffect(() => {
    const handleBeforePrint = () => setIsPrintMode(true);
    const handleAfterPrint = () => setIsPrintMode(false);

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  return (
    <Styled.Gallery>
      {isSlider && !isPrintMode ? (
        <GallerySlider {...props} />
      ) : (
        <GalleryGrid {...props} />
      )}
      {showGalleryModal && (
        <GalleryModal
          {...{
            initialGridSlide: currentSlide,
            isLeasingPDPPage,
            images,
            spinUrl,
            videoUrl,
            setShowGalleryModal,
          }}
        />
      )}
    </Styled.Gallery>
  );
};
